import { useCallback, useMemo, useState } from 'react';

import { generateShortId } from '@lyearn/core/utils/id';
import { useTranslation } from '@lyearn/i18n';
import { Trash2Fill } from '@lyearn/icons';
import { useSnackbar } from '@lyearn/ui';
import useResponse from '@/hooks/useResponse';
import { useModifyTaskCreationConfigsMutation } from '@/modules/tasks/graphql/mutations/generated/modifyTaskCreationConfigs';
import {
  ActionItemExternalEntityTypeEnum,
  CreateTaskCreationConfigInput,
  IntegrationProviderEnum,
  OkrTaskCreationConfig,
  OperationEnum,
  ProviderType,
  TaskCreationConfigTypeEnum,
  TaskCreationEntityTypeEnum,
  UpdateTaskCreationConfigInput,
} from '@/types/schema';

import { useTaskCreationConfigFileQuery } from '../../../../views/IntegrationsManager/graphql/queries/generated/taskCreationConfigFile';
import { AutoSyncFromJiraModalProps } from '../../components/AutoSyncFromJiraModal/types';
import useSubscribeToSyncConfig from '../useSubscribeToSyncConfig';

const EXTERNAL_ENTITY_TYPE_MAP: Record<string, ActionItemExternalEntityTypeEnum> = {
  [IntegrationProviderEnum.Jira]: ActionItemExternalEntityTypeEnum.JiraIssue,
};

const getInitialTaskCreationConfig = (provider: ProviderType) => ({
  providerId: provider.id,
  searchText: '',
  taskCreationType: TaskCreationConfigTypeEnum.Auto,
  id: generateShortId(),
  externalEntityType: EXTERNAL_ENTITY_TYPE_MAP[provider.integrationProvider?.provider!],
});

/**
 *
 * used in tasks (File & Folder list) via props.fileId
 *
 */

export default function useSyncFromJiraModal(props: AutoSyncFromJiraModalProps) {
  const { t } = useTranslation('Jira');
  const { provider, fileId } = props;
  const [inViewJQLQueryDialog, setInViewJQLQueryDialog] = useState<
    OkrTaskCreationConfig | CreateTaskCreationConfigInput
  >();
  const { handleResponse } = useResponse();
  const { showSnackbar } = useSnackbar();
  const closeJQLQueryDialog = useCallback(() => {
    setInViewJQLQueryDialog(undefined);
  }, []);

  const [
    { fetching: fetchingFile, data: fileData, stale: fileStale },
    executeFileTaskCreationConfig,
  ] = useTaskCreationConfigFileQuery({
    variables: {
      fileId: fileId!,
    },
    pause: !fileId,
  });

  const isFetching = fetchingFile || (fileStale && fileData?.file?.taskCreationConfigs === null);

  const [, modifyTaskCreationConfigs] = useModifyTaskCreationConfigsMutation();

  const taskCreationConfigs = useMemo(() => {
    if (fileId) {
      return fileData?.file?.taskCreationConfigs as OkrTaskCreationConfig[] | undefined;
    }

    return undefined;
  }, [fileId, fileData?.file?.taskCreationConfigs]);

  const { resetRetryCount, isRetry } = useSubscribeToSyncConfig({
    executeQuery: executeFileTaskCreationConfig,
    taskCreationConfigs,
  });

  const onAddJQLQueryClick = useCallback(
    () => setInViewJQLQueryDialog(getInitialTaskCreationConfig(provider)),
    [provider],
  );

  const refetchTaskCreationConfig = useCallback(() => {
    executeFileTaskCreationConfig();
  }, [executeFileTaskCreationConfig]);

  const onAddJQLQuery = useCallback(
    async (createTaskCreationConfig: CreateTaskCreationConfigInput) => {
      const data = await modifyTaskCreationConfigs({
        data: {
          entityId: fileId,
          entityType: fileId ? TaskCreationEntityTypeEnum.File : undefined,
          inputs: {
            createTaskCreationConfigInputs: [createTaskCreationConfig],
          },
          command: OperationEnum.Create,
          providerId: provider.id,
        },
      });

      handleResponse({
        condition: !!(fileId ? data.data?.modifyTaskCreationConfigs?.file : undefined),
        onSuccess: () => {
          showSnackbar({
            message: t('Jira:JQLQueryAddedSuccessfully'),
          });
          closeJQLQueryDialog();
        },
      });
      refetchTaskCreationConfig();
      resetRetryCount();
    },
    [
      modifyTaskCreationConfigs,
      fileId,
      provider.id,
      handleResponse,
      refetchTaskCreationConfig,
      resetRetryCount,
      showSnackbar,
      t,
      closeJQLQueryDialog,
    ],
  );

  const onUpdateJQLQuery = useCallback(
    async (updateTaskCreationConfig: UpdateTaskCreationConfigInput) => {
      const data = await modifyTaskCreationConfigs({
        data: {
          entityId: fileId,
          entityType: fileId ? TaskCreationEntityTypeEnum.File : undefined,
          inputs: {
            updateTaskCreationConfigInputs: [updateTaskCreationConfig],
          },
          command: OperationEnum.Update,
          providerId: provider.id,
        },
      });

      handleResponse({
        condition: !!(fileId ? data.data?.modifyTaskCreationConfigs?.file : undefined),
        onSuccess: () => {
          showSnackbar({
            message: t('Jira:JQLQueryUpdatedSuccessfully'),
          });
          closeJQLQueryDialog();
        },
      });
      refetchTaskCreationConfig();
    },
    [
      modifyTaskCreationConfigs,
      fileId,
      provider.id,
      handleResponse,
      refetchTaskCreationConfig,
      showSnackbar,
      t,
      closeJQLQueryDialog,
    ],
  );

  const onDeleteJQLQuery = useCallback(
    async (taskCreationConfigId: string) => {
      const data = await modifyTaskCreationConfigs({
        data: {
          entityId: fileId,
          entityType: fileId ? TaskCreationEntityTypeEnum.File : undefined,
          inputs: {
            removeTaskCreationConfigInputs: [taskCreationConfigId],
          },
          command: OperationEnum.Remove,
          providerId: provider.id,
        },
      });

      handleResponse({
        condition: !!(fileId ? data.data?.modifyTaskCreationConfigs?.file : undefined),
        onSuccess: () =>
          showSnackbar({
            message: t('Jira:JQLQueryDeleted'),
            startIcon: <Trash2Fill fillColorToken="icon-placeholder" />,
          }),
      });
      refetchTaskCreationConfig();
    },
    [
      fileId,
      handleResponse,
      modifyTaskCreationConfigs,
      provider.id,
      refetchTaskCreationConfig,
      showSnackbar,
      t,
    ],
  );

  const onAddJQLQueryModalSubmit = useCallback(
    (searchText: string) => {
      if (!inViewJQLQueryDialog?.searchText) {
        onAddJQLQuery({
          searchText,
          providerId: provider.id,
          taskCreationType: TaskCreationConfigTypeEnum.Auto,
          externalEntityType: EXTERNAL_ENTITY_TYPE_MAP[provider.integrationProvider?.provider!],
        });
        return;
      }

      onUpdateJQLQuery({
        searchText,
        taskCreationType: TaskCreationConfigTypeEnum.Auto,
        taskCreationConfigId: (inViewJQLQueryDialog as OkrTaskCreationConfig).id,
        providerId: provider.id,
      });
    },
    [
      inViewJQLQueryDialog,
      onAddJQLQuery,
      onUpdateJQLQuery,
      provider.id,
      provider.integrationProvider?.provider,
    ],
  );

  return {
    state: {
      taskCreationConfigs,
      inViewJQLQueryDialog,
      fetching: isFetching && !isRetry,
    },
    methods: {
      closeJQLQueryDialog,
      onAddJQLQueryClick,
      setInViewJQLQueryDialog,
      onAddJQLQueryModalSubmit,
      onDeleteJQLQuery,
    },
  };
}
